import { useCallback, useEffect, useRef } from 'react';
import { trackEvent } from '../services/analytics';

// Sub-second glances aren't real reading — don't emit them.
const MIN_MS = 1000;
// Walk-away guard: cap a single continuous stay so a report left open doesn't
// register as an hour on one section. Combined with the tab-hidden pause below,
// this keeps "time spent" an honest approximation.
const MAX_MS = 5 * 60_000;

/**
 * Approximate dwell-time per deck section. Whenever `section` changes, the
 * previous section's elapsed time is flushed as a `section_time` event; time
 * is paused while the tab is hidden and flushed on teardown / page unload so
 * the last section still counts. Only fires for the interactive viewer.
 */
export function useSectionTimer(section: string, slug: string, interactive: boolean) {
  const state = useRef<{ section: string; enteredAt: number | null }>({ section, enteredAt: null });

  const flush = useCallback(() => {
    const s = state.current;
    if (s.enteredAt == null || !s.section) return;
    const elapsed = Math.min(Date.now() - s.enteredAt, MAX_MS);
    s.enteredAt = null;
    if (interactive && elapsed >= MIN_MS) {
      trackEvent(slug, 'section_time', { section: s.section, ms: elapsed });
    }
  }, [interactive, slug]);

  // Section change (and initial mount): close out the previous, start the new.
  useEffect(() => {
    flush();
    state.current.section = section;
    state.current.enteredAt = Date.now();
  }, [section, flush]);

  // Pause when the tab is hidden, resume when shown, flush on unload/unmount.
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) flush();
      else state.current.enteredAt = Date.now();
    };
    const onHide = () => flush();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onHide);
    return () => {
      flush();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onHide);
    };
  }, [flush]);
}
